import type { EventDuration } from '../note/event-duration.js'

export type UndottedEasyScoreDuration = 'w' | 'h' | 'q' | '8' | '16' | '32'

/**
 * A dotted value lasts half as long again. EasyScore writes the dot after the duration (`C4/q.`).
 *
 * No dotted whole: its event duration would be a dotted measure (`'1m.'`), which Tone does not
 * parse. No dotted 32nd: it needs a 64th, which nothing here has.
 */
const dottedDurations = ['h.', 'q.', '8.', '16.'] as const
type DottedEasyScoreDuration = (typeof dottedDurations)[number]

/** @deprecated use EventDuration instead */
export type EasyScoreDuration = UndottedEasyScoreDuration | DottedEasyScoreDuration

/** Checked against the list, not the trailing dot: `lang` is user text, and `w.` must still be refused. */
export function isDottedEasyScoreDuration(note: string): note is DottedEasyScoreDuration {
  return (dottedDurations as readonly string[]).includes(note)
}

export const undotted = (note: DottedEasyScoreDuration): UndottedEasyScoreDuration =>
  note.slice(0, -1) as UndottedEasyScoreDuration

export const toTicks = (note: EasyScoreDuration, ppq = 192): number => {
  if (isDottedEasyScoreDuration(note)) {
    return toTicks(undotted(note), ppq) * 1.5
  }
  switch (note) {
    case 'w':
      return ppq * 4
    case 'h':
      return ppq * 2
    case 'q':
      return ppq
    case '8':
      return ppq / 2
    case '16':
      return ppq / 4
    case '32':
      return ppq / 8
    default:
      throw new Error(`Unknown note duration: ${note}`)
  }
}

export const toEventDuration = (note: EasyScoreDuration): EventDuration => {
  if (isDottedEasyScoreDuration(note)) {
    return `${toEventDuration(undotted(note))}.` as EventDuration
  }
  switch (note) {
    /**
     * A note value, never `'1m'`: Tone reads `m` as a MEASURE of the transport's time signature,
     * so a whole note lasted five beats in 5/4 and three in 3/4.
     */
    case 'w':
      return '1n'
    case 'h':
      return '2n'
    case 'q':
      return '4n'
    case '8':
      return '8n'
    case '16':
      return '16n'
    case '32':
      return '32n'
    default:
      throw new Error(`Unknown note duration: ${note}`)
  }
}
